import { Link } from "react-router-dom";
import {
  ShieldCheck,
  WifiOff,
  KeyRound,
  Layers,
  Sparkles,
  AlertTriangle,
  EyeOff,
  Fingerprint,
  Lock,
  Cloud,
  RefreshCw,
  Check,
  Star,
  Quote,
  Apple,
  ArrowRight,
} from "lucide-react";
import HeroIllustration from "@/components/HeroIllustration";
import SectionHeading from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const trustBadges = [
  { label: "AES-256-GCM" },
  { label: "Argon2id" },
  { label: "Zero-Knowledge" },
  { label: "Open standards" },
];

const features = [
  {
    icon: Layers,
    title: "Multi-vault support",
    desc: "Separate work, personal, and family vaults — each with its own master password.",
    span: "md:col-span-7",
  },
  {
    icon: Sparkles,
    title: "Smart password generator",
    desc: "Cryptographically strong passwords, passphrases, and PINs.",
    span: "md:col-span-5",
  },
  {
    icon: AlertTriangle,
    title: "Duplicate detection",
    desc: "Spot reused passwords across all your accounts at a glance.",
    span: "md:col-span-5",
  },
  {
    icon: ShieldCheck,
    title: "Optional breach check",
    desc: "k-anonymous lookups — your passwords never leave your device.",
    span: "md:col-span-7",
  },
  {
    icon: EyeOff,
    title: "Decoy vault",
    desc: "Plausible deniability — open a separate decoy vault under duress.",
    span: "md:col-span-4",
  },
  {
    icon: Fingerprint,
    title: "Biometric unlock",
    desc: "Touch ID, Face ID, Windows Hello, Android biometrics.",
    span: "md:col-span-4",
  },
  {
    icon: RefreshCw,
    title: "Backup & restore",
    desc: "Export encrypted .avault bundles. Restore with your master key.",
    span: "md:col-span-4",
  },
];

const faqs = [
  {
    q: "Is AVault really offline?",
    a: "Yes. Your vault file lives on your device. We don't run servers that hold your data. Sync (when enabled) happens through end-to-end encrypted bundles you control.",
  },
  {
    q: "What if I forget my master password?",
    a: "We can't recover it — that's the point. Your master password is never stored or transmitted. Use the encrypted backup feature and store a recovery copy somewhere safe.",
  },
  {
    q: "Which platforms are supported?",
    a: "macOS, Windows, Linux, iOS and Android — all sharing the same .avault format.",
  },
  {
    q: "How is data encrypted?",
    a: "Vaults use AES-256-GCM with keys derived from your master password via Argon2id (memory-hard). Each item is sealed individually.",
  },
];

const testimonials = [
  {
    name: "Mira Adesanya",
    role: "Security Engineer",
    img: "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODM3ODgwM3ww&ixlib=rb-4.1.0&q=85",
    quote:
      "AVault finally gave me a manager I trust audits with. No cloud, no surprises — just AES-256 and a master password I control.",
  },
  {
    name: "Daniel Park",
    role: "Founder, Hexline",
    img: "https://images.unsplash.com/photo-1672685667592-0392f458f46f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODM3ODgwM3ww&ixlib=rb-4.1.0&q=85",
    quote:
      "The decoy vault is the feature I didn't know I needed. Plausible deniability with zero performance hit.",
  },
  {
    name: "Sara Whitfield",
    role: "Independent journalist",
    img: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODM3ODgwM3ww&ixlib=rb-4.1.0&q=85",
    quote:
      "When sources trust you with secrets, your tooling has to be airtight. AVault works offline — exactly the threat model I need.",
  },
];

export default function Landing() {
  return (
    <div className="overflow-x-clip">
      {/* HERO */}
      <section
        className="relative pt-10 lg:pt-16 pb-24 lg:pb-32"
        data-testid="hero-section"
      >
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[500px] w-[900px] mint-glow blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 animate-rise">
            <div className="pill" data-testid="hero-badge">
              <WifiOff className="h-3.5 w-3.5" />
              Offline-first · v2.4 just shipped
            </div>
            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] text-foreground">
              Your secrets,
              <br />
              <span className="text-mint-600 dark:text-mint">sealed</span> on
              your device.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl leading-relaxed">
              AVault is a fully offline password & secrets manager. Credentials,
              cards, notes and identities — encrypted with AES-256-GCM, locked
              behind a master password only you know.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/download" data-testid="hero-cta-primary">
                <Button className="rounded-full bg-mint text-ink hover:bg-mint-300 hover:-translate-y-0.5 transition-all px-7 py-6 text-base shadow-lg shadow-mint-500/20">
                  Download AVault
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/security" data-testid="hero-cta-secondary">
                <Button
                  variant="outline"
                  className="rounded-full border-foreground/15 hover:bg-foreground/5 px-7 py-6 text-base"
                >
                  How it&apos;s secured
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-2.5">
              {trustBadges.map((b) => (
                <span
                  key={b.label}
                  className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground border border-border rounded-full px-3 py-1.5"
                  data-testid={`trust-badge-${b.label.toLowerCase()}`}
                >
                  {b.label}
                </span>
              ))}
            </div>
          </div>
          <div
            className="lg:col-span-6 animate-rise"
            style={{ animationDelay: "0.15s" }}
          >
            <HeroIllustration />
          </div>
        </div>
      </section>

      {/* OFFLINE-FIRST MESSAGE */}
      <section className="py-20 lg:py-24 border-y border-border" data-testid="offline-first-section">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="label-eyebrow mb-4">No cloud. No telemetry.</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05]">
              Built so we
              <br />
              <span className="italic font-light">never</span> see your data.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed text-lg max-w-lg">
              The vault is encrypted on your device with keys derived from your
              master password. We don&apos;t host it. We can&apos;t read it. We
              can&apos;t lose it.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: WifiOff, label: "Works offline", sub: "100% local" },
              { icon: KeyRound, label: "Master password", sub: "Never stored" },
              { icon: Cloud, label: "No servers", sub: "No accounts needed" },
              { icon: Lock, label: "Per-item seal", sub: "AES-256-GCM" },
            ].map((b, i) => (
              <div
                key={b.label}
                className="glass-card p-6 hover:-translate-y-1 transition-transform duration-300"
                data-testid={`offline-card-${i}`}
              >
                <div className="h-10 w-10 rounded-xl bg-mint/40 dark:bg-mint/20 flex items-center justify-center text-mint-700 dark:text-mint">
                  <b.icon className="h-5 w-5" />
                </div>
                <p className="mt-4 font-heading font-semibold">{b.label}</p>
                <p className="text-sm text-muted-foreground mt-1">{b.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE BENTO */}
      <section className="py-24 lg:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="What's inside"
            title="A vault built for the way you actually use passwords."
            description="Every feature ships locally. Nothing is enabled by sending data to a server you don't run."
          />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-14">
            {features.map((f) => (
              <div
                key={f.title}
                className={`glass-card p-7 lg:p-8 hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden ${f.span}`}
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-mint/30 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative">
                  <div className="h-11 w-11 rounded-xl bg-foreground/5 flex items-center justify-center text-foreground">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CROSS-PLATFORM */}
      <section className="py-20 lg:py-24" data-testid="platforms-section">
        <div className="max-w-7xl mx-auto px-6 glass-card p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -bottom-20 h-72 w-72 rounded-full mint-glow blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="label-eyebrow mb-4">Cross-platform</p>
              <h3 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-[1.1]">
                One vault. Every device you actually use.
              </h3>
              <p className="mt-4 text-muted-foreground leading-relaxed max-w-lg">
                AVault opens the same encrypted .avault file on macOS, Windows,
                Linux, iOS and Android — sync stays under your control.
              </p>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {["macOS", "Windows", "Linux", "iOS", "Android"].map((p) => (
                <div
                  key={p}
                  className="aspect-square rounded-2xl border border-border flex flex-col items-center justify-center text-xs font-semibold tracking-wide bg-card hover:border-mint hover:bg-mint/10 transition-all"
                  data-testid={`platform-${p.toLowerCase()}`}
                >
                  <Apple className={`h-5 w-5 mb-2 ${p === "macOS" || p === "iOS" ? "" : "opacity-0"}`} />
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading
            eyebrow="Trusted by careful people"
            title="The privacy-minded use AVault."
          />
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                className="glass-card p-7 flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-6 w-6 text-mint-500" />
                <p className="mt-4 text-sm leading-relaxed text-foreground">
                  {t.quote}
                </p>
                <div className="mt-6 flex items-center gap-3 pt-5 border-t border-border">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-0.5 text-mint-500">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} className="h-3.5 w-3.5 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading
            eyebrow="Common questions"
            title="Answers, before you ask."
            align="center"
          />
          <Accordion type="single" collapsible className="mt-12">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-border"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-10">
            <Link to="/faq">
              <Button variant="outline" className="rounded-full" data-testid="faq-see-all">
                See all FAQs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24 lg:pb-32" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-[2rem] glass-card p-10 lg:p-16 overflow-hidden">
            <div className="absolute inset-0 mint-glow" />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <p className="label-eyebrow mb-4">Free to start</p>
                <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.0]">
                  Take ownership of your secrets.
                </h2>
                <p className="mt-5 text-muted-foreground text-lg max-w-lg leading-relaxed">
                  Set up your first vault in under 60 seconds. Free, forever.
                  Upgrade to Pro when you need more.
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <Link to="/download" data-testid="final-cta-download">
                  <Button className="rounded-full bg-mint text-ink hover:bg-mint-300 px-8 py-6 text-base shadow-xl shadow-mint-500/30">
                    Download free
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-mint-500" /> No account
                  required
                  <Check className="h-3.5 w-3.5 text-mint-500 ml-2" /> Open
                  format
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
